import {gql} from '@apollo/client';
import React from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CheckSampleStatusParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {
  ContainerContentTablesQuery,
  ContainerContentTablesQueryVariables,
} from 'samples/__generated/ContainerContentTablesQuery';

import ContainerContentDetailsTable, {ContainerContentNode} from './ContainerContentDetailsTable';

export interface QueryingContainerContentTablesProps {
  containerId: string;
  checkSampleStatus: (params: CheckSampleStatusParams) => Promise<boolean>;
  disableEditsWithTooltip?: string | null;
  isWritable?: boolean;
}

interface ContainerContentTablesProps extends QueryingContainerContentTablesProps {
  containerContentNodes: Array<ContainerContentNode>;
}

export const CONTAINER_CONTENT_TABLES_QUERY = gql`
  query ContainerContentTablesQuery($containerIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        contents {
          id
          lotNumber
          concentrationJson
          containedItem {
            ... on RegistryItemInterface {
              id
              name
              editUrl
              tagsJson
              tagSchema {
                id
                showResidues
              }
              archiveRecord {
                purpose
              }
            }
            ... on ProteinNode {
              id
              currentVersionJsonIfShowResidues
            }
            ... on SequenceNode {
              id
              basesIfShowResidues
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Primes the Apollo with the bulk GQL query for the ContainerContentTables component.
 *
 * Useful for when loading the containers in a grid view to avoid having to network fetch each cell when the user clicks around.
 */
export async function bulkPreloadContainerContentTablesQueryForContents(
  containerIds: Array<string>
): Promise<void> {
  await doQuerySafe<ContainerContentTablesQuery, ContainerContentTablesQueryVariables>(
    {query: CONTAINER_CONTENT_TABLES_QUERY},
    {variables: {containerIds}}
  );
}

function QueryingContainerContentTables(props: QueryingContainerContentTablesProps): JSX.Element {
  const result = useBenchlingQuery<ContainerContentTablesQuery, ContainerContentTablesQueryVariables>(
    CONTAINER_CONTENT_TABLES_QUERY,
    {
      variables: {containerIds: [props.containerId]},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  let containerContentNodes: Array<ContainerContentNode> = [];
  if (
    result.type === QueryResultType.SUCCESS &&
    // TODO(BNCH-107814): The following condition works aroudn a bug in apollo client where it returns partial
    // cached data. It avoids a crash rendering the page, BNCH-123590. The bug should be fixed in apollo
    // 3.10.4 and later. https://github.com/apollographql/apollo-client/issues/11400
    result.data.containers.every(
      (container) =>
        isInaccessible(container) ||
        container.contents.every(
          (content) =>
            isInaccessible(content) ||
            isInaccessible(content.containedItem) ||
            content.containedItem.tagsJson != null
        )
    )
  ) {
    const [container] = result.data.containers;
    if (!isInaccessible(container)) {
      containerContentNodes = container.contents;
    }
  }

  return <ContainerContentTables {...props} containerContentNodes={containerContentNodes} />;
}

function ContainerContentTables({
  containerId,
  containerContentNodes,
  checkSampleStatus,
  disableEditsWithTooltip,
  isWritable,
}: ContainerContentTablesProps): JSX.Element {
  return (
    <div className="u-spacing-bottom">
      {containerContentNodes.map((containerContent) => {
        if (!isInaccessibleInfo(containerContent.containedItem)) {
          // bioentity
          // Note that if the bioentities passed in are summaries, residues will be undefined and
          // not displayed.
          const bioentity = containerContent.containedItem;
          let residues = null;
          if (bioentity.__typename === 'SequenceNode') {
            residues = bioentity.basesIfShowResidues;
          } else if (bioentity.__typename === 'ProteinNode') {
            residues = bioentity.currentVersionJsonIfShowResidues?.aminoAcids;
          }
          return (
            <ContainerContentDetailsTable
              containerId={containerId}
              containerContent={containerContent}
              key={bioentity.id}
              name={
                <span>
                  {bioentity.name}
                  <ArchiveRecordLabel item={bioentity} />
                </span>
              }
              residues={residues}
              showResidues={bioentity.tagSchema?.showResidues}
              tags={bioentity.tagsJson.filter((tagJson) => Object.keys(tagJson).length)}
              url={bioentity.editUrl}
              checkSampleStatus={checkSampleStatus}
              disableEditsWithTooltip={disableEditsWithTooltip}
              isWritable={isWritable}
            />
          );
        } else {
          return (
            <ContainerContentDetailsTable
              containerId={containerId}
              containerContent={containerContent}
              key={containerContent.containedItem.invalidId}
              name="(No access)"
              tags={[]}
              url={null}
              checkSampleStatus={checkSampleStatus}
              disableEditsWithTooltip={
                disableEditsWithTooltip ?? 'Cannot update content without access to the contained item'
              }
              isWritable={false}
            />
          );
        }
      })}
    </div>
  );
}

export default QueryingContainerContentTables;
